import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { Skeleton } from "@templestay/ui";
import type { Language } from "@templestay/i18n";
import type { Test, Survey } from "@templestay/db";
import { useAuthStore } from "@/store/auth.js";

const LANG_LABELS: Record<string, string> = { ko: "🇰🇷", en: "🇺🇸", zh: "🇨🇳", ja: "🇯🇵" };

type EntityType = "test" | "survey";

type Response = {
  id: string;
  language: Language;
  result_type_id?: string | null;
  started_at: string;
  completed_at: string | null;
  country_code: string | null;
  personal_info: Record<string, unknown> | null;
};

type ResponsesData = {
  responses: Response[];
  total: number;
};

export function ResponsesPage() {
  const { activeTenantId } = useAuthStore();
  const [entityType, setEntityType] = useState<EntityType>("test");
  const [selectedId, setSelectedId] = useState("");
  const [reveal, setReveal] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const { data: tests } = useQuery({
    queryKey: ["tests", activeTenantId],
    queryFn: () => api.get<Test[]>("/api/tests"),
    enabled: !!activeTenantId,
  });

  const { data: surveys } = useQuery({
    queryKey: ["surveys", activeTenantId],
    queryFn: () => api.get<Survey[]>("/api/surveys"),
    enabled: !!activeTenantId,
  });

  const entityList = entityType === "test" ? tests : surveys;

  const { data, isLoading } = useQuery({
    queryKey: ["responses", activeTenantId, entityType, selectedId, reveal, offset],
    queryFn: () =>
      api.get<ResponsesData>(
        `/api/stats/responses?survey_id=${selectedId}&type=${entityType}&limit=${LIMIT}&offset=${offset}&reveal=${reveal}`
      ),
    enabled: !!activeTenantId && !!selectedId,
  });

  const totalPages = Math.ceil((data?.total ?? 0) / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const showResultCol = entityType === "test";

  function handleTypeChange(t: EntityType) {
    setEntityType(t);
    setSelectedId("");
    setOffset(0);
  }

  function handleEntityChange(id: string) {
    setSelectedId(id);
    setOffset(0);
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">응답자 리스트</h1>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
          <div
            role="switch"
            aria-checked={reveal}
            onClick={() => setReveal((v) => !v)}
            className={`relative h-5 w-9 rounded-full transition-colors ${reveal ? "bg-brand-500" : "bg-gray-200"}`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${reveal ? "translate-x-4" : "translate-x-0.5"}`}
            />
          </div>
          <span>개인정보 표시</span>
          {reveal && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">주의</span>
          )}
        </label>
      </div>

      {/* 유형 탭 + 항목 선택 */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-500">유형</label>
          <div className="flex rounded-lg border border-gray-200 bg-white p-1">
            {(["test", "survey"] as const).map((t) => (
              <button
                key={t}
                onClick={() => handleTypeChange(t)}
                className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${
                  entityType === t ? "bg-brand-500 text-white" : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t === "test" ? "🎯 검사" : "📋 설문"}
              </button>
            ))}
          </div>
        </div>

        <div className="min-w-0 flex-1 max-w-sm">
          <label className="mb-1.5 block text-xs font-medium text-gray-500">
            {entityType === "test" ? "검사 선택" : "설문 선택"}
          </label>
          <select
            value={selectedId}
            onChange={(e) => handleEntityChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">— 선택하세요 —</option>
            {entityList?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} {e.is_active ? "✓" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : !data ? (
        <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
          <p className="text-gray-400">
            {selectedId ? "응답이 없습니다." : `${entityType === "test" ? "검사" : "설문"}를 선택하세요.`}
          </p>
        </div>
      ) : data.responses.length === 0 ? (
        <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
          <p className="text-gray-400">응답이 없습니다.</p>
        </div>
      ) : (
        <>
          <div className="mb-3 text-sm text-gray-500">
            전체 {data.total.toLocaleString()}개 중 {offset + 1}–{Math.min(offset + LIMIT, data.total)}번째
          </div>

          <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">언어</th>
                  {reveal && <th className="px-4 py-3 text-left">이름</th>}
                  {reveal && <th className="px-4 py-3 text-left">나이</th>}
                  {reveal && <th className="px-4 py-3 text-left">성별</th>}
                  {reveal && <th className="px-4 py-3 text-left">국적</th>}
                  {showResultCol && <th className="px-4 py-3 text-left">결과</th>}
                  <th className="px-4 py-3 text-left">완료</th>
                  <th className="px-4 py-3 text-left">시작일시</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.responses.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{LANG_LABELS[r.language] ?? r.language}</td>
                    {reveal && (
                      <td className="px-4 py-3 text-gray-700">
                        {(r.personal_info?.["name"] as string | undefined) ?? "—"}
                      </td>
                    )}
                    {reveal && (
                      <td className="px-4 py-3 text-gray-700">
                        {(r.personal_info?.["age"] as number | undefined) ?? "—"}
                      </td>
                    )}
                    {reveal && (
                      <td className="px-4 py-3 text-gray-700">
                        {(r.personal_info?.["gender"] as string | undefined) ?? "—"}
                      </td>
                    )}
                    {reveal && (
                      <td className="px-4 py-3 text-gray-700">
                        {(r.personal_info?.["nationality"] as string | undefined) ?? "—"}
                      </td>
                    )}
                    {showResultCol && (
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {r.result_type_id ? r.result_type_id.slice(-8) : "—"}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {r.completed_at ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {new Date(r.started_at).toLocaleString("ko-KR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40"
              >
                ← 이전
              </button>
              <span className="text-sm text-gray-500">{currentPage} / {totalPages}</span>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={currentPage >= totalPages}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40"
              >
                다음 →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
